import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../Api";
import { generatePDF } from "../utils/pdfGenerator";
import { useFlash } from "../context/flashcontext";
import { isOwnerOrFinance } from "../utils/role-check";
import { useAuth } from "../context/authcontext";
import { useProjectRole } from "./useProjectRole";

export function useTimesheetDetail() {
    const { t } = useTranslation();
    const [rateType, setRateType] = useState("hourly");
    const { id } = useParams();
    const { showFlash } = useFlash?.() || {};
    const [timesheet, setTimesheet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const baseUrl = (window.env && window.env.REACT_APP_BASE_URL) || process.env.REACT_APP_BASE_URL || "http://localhost:8080";
    const [materialsTotal, setMaterialsTotal] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTimesheetId, setSelectedTimesheetId] = useState(null);
    const { auth } = useAuth();

    const fetchTimesheet = useCallback(async () => {
        try {
          const response = await api.get(`/timesheets/${id}`);
          setTimesheet(response.data);
          setRateType(response.data.rate_type || "hourly");
          if (response.data.materials) {
            setMaterialsTotal(response.data.materials.reduce((acc, cur) => acc + ((parseFloat(cur.quantity) || 0) * (parseFloat(cur.cost) || 0)), 0));
          }
        } catch (err) {
          // Fallback for voorman: try to find timesheet in all voorman projects
          if (err.response?.status === 404 && auth?.role === "voorman") {
            try {
              // Fetch all voorman projects
              const voormanProjectsRes = await api.get(`/users/${auth.userId}/voorman-projects`);
              const voormanProjects = voormanProjectsRes.data || [];
              let found = null;
              for (const project of voormanProjects) {
                const tsRes = await api.get(`/timesheets/project/${project.id}?include=user,project`);
                if (Array.isArray(tsRes.data)) {
                  found = tsRes.data.find(ts => String(ts.id) === String(id));
                  if (found) break;
                }
              }
              if (found) {
                setTimesheet(found);
                setRateType(found.rate_type || "hourly");
                if (found.materials) {
                  setMaterialsTotal(found.materials.reduce((acc, cur) => acc + ((parseFloat(cur.quantity) || 0) * (parseFloat(cur.cost) || 0)), 0));
                }
                setError(null);
                return;
              } else {
                setTimesheet(null);
              }
            } catch (voormanErr) {
              setError("Failed to load timesheet.");
            }
          } else {
            setError("Failed to load timesheet.");
          }
        } finally {
          setLoading(false);
        }
      }, [id, auth]);

    useEffect(() => {
        fetchTimesheet();
    }, [fetchTimesheet]);

    // Helper to get ISO week number and year from a date string
    function getWeekAndYear(dateString) {
        if (!dateString) return { week_number: undefined, year: undefined };
        const date = new Date(dateString);
        // ISO week number calculation
        const tempDate = new Date(date.getTime());
        tempDate.setHours(0, 0, 0, 0);
        // Thursday in current week decides the year
        tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
        const week1 = new Date(tempDate.getFullYear(), 0, 4);
        const weekNumber = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
        return { week_number: weekNumber, year: tempDate.getFullYear() };
    }

    // Patch timesheet with week_number and year if missing or zero
    let patchedTimesheet = timesheet;
    if (timesheet && (!timesheet.week_number || !timesheet.year)) {
        const { week_number, year } = getWeekAndYear(timesheet.week_start_date);
        patchedTimesheet = { ...timesheet, week_number, year };
    }

    const parseJSON = (data, fallback = []) => {
        try {
            return typeof data === "string" ? JSON.parse(data) : data;
        } catch (error) {
            return fallback;
        }
    };

    const daysOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
    const daysTranslated = {
        "monday": t('timesheet.monday'),
        "tuesday": t('timesheet.tuesday'),
        "wednesday": t('timesheet.wednesday'),
        "thursday": t('timesheet.thursday'),
        "friday": t('timesheet.friday'),
        "saturday": t('timesheet.saturday'),
        "sunday": t('timesheet.sunday'),
    };
    const statusTranslated = {
        "draft": t('timesheet.draft'),
        "approved": t('timesheet.approved'),
        "rejected": t('timesheet.rejected'),
        "pending": t('timesheet.pending'),
        "approved_by_voorman": t('timesheet.approvedByVoorman')
    };

    const extraWorkItems = parseJSON(timesheet?.extra_work);
    const materials = parseJSON(timesheet?.materials);

    const handleGeneratePDF = async () => {
        if (!timesheet?.user || !timesheet?.project) return;
        const startOfWeek = new Date(timesheet.week_start_date);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        // Calculate totals based on rate type
        const totalHours = timesheet.rate_type === "meter"
            ? Object.values(timesheet.meters_worked || {}).reduce((sum, meters) => sum + meters, 0)
            : Object.values(timesheet.hours_worked || {}).reduce((sum, hours) => sum + hours, 0);
        
        // Convert worked data to array format for PDF
        const hoursData = daysOrder.map((day) => (timesheet.hours_worked?.[day] || 0));
        const metersData = daysOrder.map((day) => (timesheet.meters_worked?.[day] || 0));

        const photos = Array.isArray(timesheet.photos) ? timesheet.photos : [];
        const getFullUrl = (url) => {
            if (!url) return null;
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            return `${baseUrl}${url.replace("./", "/")}`;
        };
        const imagePromises = photos.map(async (photoUrl) => {
            try {
                const fullUrl = getFullUrl(photoUrl);
                const response = await fetch(fullUrl);
                const blob = await response.blob();
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(blob);
                });
            } catch (error) {
                return null;
            }
        });
        const materialImagesPromises = (timesheet.materials || []).flatMap((material) =>
            (material.receipts || []).map(async (receipt) => {
                try {
                    const fullUrl = getFullUrl(receipt);
                    const response = await fetch(fullUrl);
                    const blob = await response.blob();
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    return null;
                }
            })
        );
        // Download extraWork images grouped by item
        const extraWorkImages = await Promise.all((extraWorkItems || []).map(async (item) => {
            const images = await Promise.all((item.photos || []).map(async (photoUrl) => {
                try {
                    const fullUrl = getFullUrl(photoUrl);
                    const response = await fetch(fullUrl);
                    const blob = await response.blob();
                    return await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    return null;
                }
            }));
            return {
                omschrijving: item.omschrijving,
                opmerkingen: item.opmerkingen,
                images: images.filter(Boolean),
            };
        }));
        // Download material receipt images grouped by material
        const materialReceiptImages = await Promise.all((materials || []).map(async (material) => {
            const images = await Promise.all((material.receipts || []).map(async (receipt) => {
                try {
                    const fullUrl = getFullUrl(receipt);
                    const response = await fetch(fullUrl);
                    const blob = await response.blob();
                    return await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                } catch (error) {
                    return null;
                }
            }));
            return {
                name: material.name,
                description: material.description,
                images: images.filter(Boolean),
            };
        }));
        const images = await Promise.all(imagePromises);
        const materialImages = await Promise.all(materialImagesPromises);

        await generatePDF({
            user: timesheet.user,
            selectedProject: timesheet.project,
            hours: hoursData,
            meters: metersData,
            extraWork: extraWorkItems,
            materials: materials,
            totalHours: totalHours,
            totalCosts: materialsTotal || 0,
            weekRange: { startOfWeek, endOfWeek },
            images: images.filter(img => img !== null),
            materialImages: materialImages.filter(img => img !== null),
            status: timesheet.status,
            statusTranslated: statusTranslated[timesheet.status],
            extraWorkImages,
            materialReceiptImages,
            rateType: timesheet.rate_type || "hourly"
        });
    };

  const handleApprove = async (id) => {
    try {
      await api.put(`/timesheets/${id}/approve`);
      showFlash(t('success.timesheetApproved'), "success");
      await fetchTimesheet(); // Refresh the timesheet data after approval
    } catch (err) {
      showFlash(t('errors.approveTimesheetFailed'), "error");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.put(`/timesheets/${id}/reject`);
      showFlash(t('success.timesheetRejected'), "success");
      await fetchTimesheet(); // Refresh the timesheet data after approval
    } catch (err) {
      showFlash(t('errors.rejectTimesheetFailed'), "error");
    }
  };

  const handleDeleteConfirmed = async () => {
    try {
      await api.delete(`/timesheets/${selectedTimesheetId}`);
      if (typeof showFlash === 'function') {
        showFlash(t('success.timesheetDeleted'), "success");
      }
    } catch (err) {
      if (typeof showFlash === 'function') {
        showFlash(t('errors.deleteTimesheetFailed'), "error");
      }
    } finally {
      setShowDeleteModal(false);
      setSelectedTimesheetId(null);
    }
  };

    // Use the shared hook to get the current user's project role for this timesheet's project
    const projectRole = useProjectRole(patchedTimesheet?.project_id);

    return {
        rateType,
        timesheet: patchedTimesheet,
        setTimesheet,
        loading,
        error,
        materialsTotal,
        daysOrder,
        daysTranslated,
        statusTranslated,
        extraWorkItems,
        materials,
        showDeleteModal,
        fetchTimesheet,
        handleDeleteConfirmed,
        handleReject,
        handleApprove,
        handleGeneratePDF,
        projectRole
    };
}
